import { Button, Flex, Modal, Pagination, Segmented, Switch, Table, Tag, Tooltip, Typography } from "antd"

import {
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData
} from '@tanstack/react-query'
import { APIService } from "../services/API.service"
import { Campaign, CampaignDetail as CampaignDetailData } from "../interfaces/Campaign.interface"
import { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { useState, useMemo } from "react"
import { PushRole, useAuth } from "../hooks/useAuth"
import { CampaignDetail } from "./CampaignDetail"
import { CopyOutlined, EyeOutlined, StopOutlined } from "@ant-design/icons"
import { CampaignFilterOptions, FilterCampaignStatus } from "../interfaces/FilterCampaignTypes"
import { validateEnv } from "../utils/validateEnv";

const { REACT_APP_CAMPAIGN_CANCEL_WINDOW_MINUTES } = validateEnv();

const CANCEL_WINDOW_MS = REACT_APP_CAMPAIGN_CANCEL_WINDOW_MINUTES * 60 * 1000;

export const CampaignDetailToCampaign = (campaignDetail:CampaignDetailData):Campaign => {
    let result:any = {...campaignDetail};
    result.audiences = campaignDetail.audiences?.map(x=>x.id) ?? [];
    return result;
}
export const getCampaignColumns = (props:{applicationId:string, showDetail:(campaign:Campaign)=>void, cloneEnabled:boolean, cloneCampaign: (campaign:Campaign)=>void, showCancel:(campaign:CampaignDetailData)=>void, getCancelDisabledReason:(campaign:CampaignDetailData)=>string|undefined, cancelEnabled:boolean}): ColumnsType<CampaignDetailData> => {

    return [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Audience', dataIndex: 'audience', key: 'audiences', render:(value:any, record:CampaignDetailData)=> (<Flex gap={5} wrap={true} >{(record.audiences ?? []).map(x => (<Tag key={x.id}>{`${x.name}`}</Tag>))}</Flex>)},
        { title: 'Delivery Status', dataIndex: 'status', key: 'status'},
        { title: 'Scheduled At', dataIndex: 'deliverAt', key: 'deliverAt', render: (value: string) => dayjs(value).format('D MMMM YYYY h:mm A') },
        { title: 'Action', key: "action", render: (value: CampaignDetailData) => {
            const cancelReason = props.getCancelDisabledReason(value);
            const cancelButton = (
                props.cancelEnabled &&  <Button
                    title="Cancel"
                    onClick={() => props.showCancel(value)}
                    icon={<StopOutlined />}
                    danger
                    disabled={Boolean(cancelReason)}
                />
            );
            const cancelButtonWithTooltip = cancelReason ? (
                <Tooltip title={cancelReason}>
                    {cancelButton}
                </Tooltip>
            ) : cancelButton;
            return (
                <Flex>
                    <Button.Group>
                        <Button title="View" onClick={()=>props?.showDetail(CampaignDetailToCampaign(value))}><EyeOutlined /></Button>
                        {props.cloneEnabled && <Button title="Clone" onClick={()=>props?.cloneCampaign(CampaignDetailToCampaign(value))}><CopyOutlined/></Button>}
                        {cancelButtonWithTooltip}
                    </Button.Group>
                </Flex>
            )
        }}
    ]
}

export interface CampaignListProps {

    applicationId: string;

    // called when + by the list requests a campaign to be edited
    onEdit: (campaign: Campaign) => void;

    // called when the list request a campaign to be cloned
    onClone: (campaign:Campaign) => void;

    // called when + by the list requests a campaign to be viewed in detail
    onSelect: (campaign: Campaign) => void;
}

export const CampaignList = (props: CampaignListProps) => {

    const [campaignDetail, setCampaignDetail] = useState<Campaign|null>(null)
    const [showCampaignDetail, setShowCampaignDetail] = useState<boolean>(false);
    const [campaignFilter, setCampaignFilter] = useState<CampaignFilterOptions>({ limit: 10, offset: 0});
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [deliveryStatusFilter, setDeliveryStatusFilter] = useState<string | null>(null);
    const [showHidden, setShowHidden] = useState<boolean>(false);

    const auth = useAuth();
    const queryClient = useQueryClient();
    const cancellationMutation = useMutation({
        mutationFn: (campaignId: string) => auth.api.cancelCampaign(props.applicationId, campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['campaigns', props.applicationId] });
        }
    });

    const getCancelDisabledReason = (campaign: CampaignDetailData): string | undefined => {
        if (!campaign) {
            return undefined;
        }
        if (campaign.status === "Cancelled") {
            return "This campaign is already cancelled.";
        }
        if (campaign.status === "Complete") {
            return "This campaign is already sent.";
        }

        const deliverAtMs = dayjs(campaign.deliverAt).valueOf();
        if (Date.now() >= deliverAtMs - CANCEL_WINDOW_MS) {
            return "Campaign cannot be cancelled as the system is preparing to send it.";
        }

        return undefined;
    };

    const showCancelModal = (campaign: CampaignDetailData) => {
        Modal.confirm({
            title: "Cancel Campaign",
            content: "Are you sure you want to cancel this campaign?",
            okText: "Yes",
            cancelText: "No",
            onOk: () => cancellationMutation.mutateAsync(campaign.id)
        });
    };

    // Dynamic filter options using enum values
    const filterOptions = useMemo(() => {
        return ["All", ...Object.values(FilterCampaignStatus)];
    }, []);

    // Queries
    const campaignQuery = useQuery({ 
        staleTime: 5000,
        refetchInterval: 30000,
        queryKey: [
            'campaigns',
            props.applicationId,
            'offset',
            campaignFilter.offset,
            'limit',
            campaignFilter.limit,
            'status',
            deliveryStatusFilter ?? "unknown",
            'hidden',
            !showHidden  // Invert the value for the API call
        ], 
        queryFn: () => auth.api.getCampaigns(
            props.applicationId, 
            {
                ...campaignFilter,
                ...(deliveryStatusFilter && { status: deliveryStatusFilter as FilterCampaignStatus }),
                hidden: !showHidden  // Invert the value for the API call
            } as CampaignFilterOptions
        ),
        placeholderData: keepPreviousData,
    });
    
    const dismissCampaignDetail = ()=>{
        setShowCampaignDetail(false);
    }

    const displayCampaignDetail = (c:Campaign)=>{
        setCampaignDetail(c);
        setShowCampaignDetail(true);
        console.log("showDetail", c);
    }

    const cloneCampaign = (c:Campaign)=> {
        props.onClone(c);
    }
    
    const onPaginationChange = (page:number, pageSize:number)=>{
        // Ant Design Pagination uses 1-based page numbers
        // Convert to 0-based offset for API
        const newOffset = (page - 1) * pageSize;
        setCampaignFilter({ ...campaignFilter, offset: newOffset, limit: pageSize });
        setCurrentPage(page);
    }

    const onDeliveryStatusFilterChange = (value: string) => {
        setDeliveryStatusFilter(value);
        setCampaignFilter({ ...campaignFilter, offset: 0 });
        setCurrentPage(1);
    };

    return (
        <Flex vertical gap={10}>
            <Flex gap={10} align="center">
                <Typography.Text>Filter by Delivery Status:</Typography.Text>
                <Segmented
                    options={filterOptions}
                    onChange={(value: string | number) => {
                        const stringValue = String(value);
                        setDeliveryStatusFilter(stringValue === "All" ? null : stringValue);
                        setCampaignFilter({ ...campaignFilter, offset: 0 });
                        setCurrentPage(1);
                    }}
                />
                {auth.hasClaims(props.applicationId, PushRole.CanUseDeveloperTools) && (
                    <>
                        <Typography.Text>Show Hidden:</Typography.Text>
                        <Switch checked={showHidden} onChange={setShowHidden} />
                    </>
                )}
            </Flex>
            <Table
                columns={getCampaignColumns({ 
                    applicationId: props.applicationId, 
                    showDetail:displayCampaignDetail, 
                    cloneEnabled: auth.hasClaims(props.applicationId,PushRole.CanCreateACampaign),
                    cloneCampaign: cloneCampaign,
                    showCancel: showCancelModal,
                    cancelEnabled: auth.hasClaims(props.applicationId,PushRole.CanCancelACampaign),
                    getCancelDisabledReason: getCancelDisabledReason
                })}
                size={"middle"}
                loading={campaignQuery.isFetching}
                dataSource={campaignQuery.data?.results ?? []}
                rowKey={(campaign:CampaignDetailData)=>campaign.id + campaign.name}
                pagination={false}
            />
            <Pagination
                current={currentPage}
                onChange={onPaginationChange}
                total={campaignQuery.data?.total}
                pageSize={campaignFilter.limit}
                pageSizeOptions={[10,20,50,100]}
                showSizeChanger
            />

            {showCampaignDetail ? <CampaignDetail applicationId={props.applicationId} campaign={campaignDetail} isOpen={showCampaignDetail} onDismiss={()=>dismissCampaignDetail()} ></CampaignDetail> : <></>}
        </Flex>
    )

}